import type {NormalizedCacheObject} from '@apollo/client';
import {type ApolloClientProxy, type BaseHttpCodes, EntityBaseGqlService, type ServiceResponsePayload} from '@igp/shared/api';
import type {
    BoUser,
    BoUserInput,
    GroupInput,
    Mutation,
    MutationAddBoUserArgs,
    MutationResetPasswordArgs,
    MutationUnlinkAgentArgs,
    MutationUpdateAccountStatusArgs,
    MutationUpdateBoUserArgs,
    Query,
    QueryGetBoUsersArgs,
    Subscription,
} from '@igp/shared/contract/pam';
import type {IMessageLocalizer} from '@igp/shared/localization';
import {map} from '@igp/shared/otel';
import type {EntityFetchRequestPayload, EntityFetchServiceResponsePayload, IEntityReadService} from '@igp/shared/redux/entity';
import type {EntityType} from '@models';
import {inject, injectable} from 'inversify';
import type {Observable} from 'rxjs';

import {ServiceTypes} from '@ioc-container';

import {BoUserRequestBuilder} from './requestBuilder';
import type {
    BoUserAddPayload,
    BoUserEditPayload,
    BoUserQueryFields,
    BoUserResetPasswordPayload,
    BoUserServerFilterKeys,
    BoUserUnlinkAgentPayload,
    BoUserUpdateAccountStatusPayload,
} from './types';

export interface IBoUserService extends IEntityReadService<EntityType> {
    addBoUser(payload: BoUserAddPayload): Observable<ServiceResponsePayload<BoUserAddPayload, string>>;
    updateBoUser(payload: BoUserEditPayload): Observable<ServiceResponsePayload<BoUserEditPayload, string>>;
    resetPassword(payload: BoUserResetPasswordPayload): Observable<ServiceResponsePayload<BoUserResetPasswordPayload, string>>;
    updateAccountStatus(
        payload: BoUserUpdateAccountStatusPayload
    ): Observable<ServiceResponsePayload<BoUserUpdateAccountStatusPayload, string>>;
    unlinkAgent(payload: BoUserUnlinkAgentPayload): Observable<ServiceResponsePayload<BoUserUnlinkAgentPayload, string>>;
}

@injectable()
export class BoUserService
    extends EntityBaseGqlService<
        EntityType,
        Query,
        QueryGetBoUsersArgs,
        BoUserQueryFields,
        Mutation,
        Subscription,
        BaseHttpCodes,
        BoUserServerFilterKeys
    >
    implements IBoUserService
{
    private readonly _mutationRequestBuilder: BoUserRequestBuilder;

    constructor(
        @inject(ServiceTypes.ApolloClientIGP) client: ApolloClientProxy<NormalizedCacheObject>,
        @inject(ServiceTypes.MessageLocalizer) messageLocalizer: IMessageLocalizer
    ) {
        const requestBuilder = new BoUserRequestBuilder();
        super(client, requestBuilder, messageLocalizer);
        this._mutationRequestBuilder = requestBuilder;
    }

    get(
        requestPayload: EntityFetchRequestPayload<EntityType, BoUserQueryFields>
    ): Observable<EntityFetchServiceResponsePayload<EntityType, BoUser>> {
        return this.fetchData(requestPayload).pipe(
            map(({responsePayload, ...response}) => {
                return {
                    ...response,
                    requestPayload,
                    responsePayload: {items: responsePayload?.getBoUsers?.items ?? [], total: responsePayload?.getBoUsers?.total ?? 0},
                };
            })
        );
    }

    addBoUser(payload: BoUserAddPayload): Observable<ServiceResponsePayload<BoUserAddPayload, string>> {
        const groups: GroupInput[] = [{id: payload.roleGroupId}, {id: payload.aalGroupId}]?.filter(Boolean);
        const bo_user: BoUserInput = {
            attributes: payload.playerId ? {player_id: payload.playerId} : null,
            credentials: {temporary: true, value: payload.password},
            email: payload.email,
            first_name: payload.firstName,
            groups,
            last_name: payload.lastName,
            username: payload.username,
        };
        return this._service.mutate<MutationAddBoUserArgs>(this._mutationRequestBuilder.getAddMutation(), {bo_user}).pipe(
            map(({responsePayload, ...response}) => {
                return {
                    ...response,
                    requestPayload: payload,
                    responsePayload: responsePayload.addBoUser,
                };
            })
        );
    }

    updateBoUser(payload: BoUserEditPayload): Observable<ServiceResponsePayload<BoUserEditPayload, string>> {
        const groups: GroupInput[] = [{id: payload.roleGroupId}, {id: payload.aalGroupId}]?.filter(i => i.id);
        const bo_user: BoUserInput = {
            attributes: payload.playerId ? {player_id: payload.playerId} : null,
            email: payload.email,
            first_name: payload.firstName,
            groups,
            last_name: payload.lastName,
            username: payload.username,
        };
        return this._service
            .mutate<MutationUpdateBoUserArgs>(this._mutationRequestBuilder.getUpdateMutation(), {
                bo_user_id: payload.boUserId,
                bo_user,
            })
            .pipe(
                map(({responsePayload, ...response}) => {
                    return {
                        ...response,
                        requestPayload: payload,
                        responsePayload: responsePayload.updateBoUser,
                    };
                })
            );
    }

    resetPassword(payload: BoUserResetPasswordPayload): Observable<ServiceResponsePayload<BoUserResetPasswordPayload, string>> {
        return this._service
            .mutate<MutationResetPasswordArgs>(this._mutationRequestBuilder.getResetPasswordMutation(), {
                bo_user_id: payload.boUserId,
                credentials: {temporary: true, value: payload.newPassword},
            })
            .pipe(
                map(({responsePayload, ...response}) => {
                    return {
                        ...response,
                        requestPayload: payload,
                        responsePayload: responsePayload.resetPassword,
                    };
                })
            );
    }

    updateAccountStatus(
        payload: BoUserUpdateAccountStatusPayload
    ): Observable<ServiceResponsePayload<BoUserUpdateAccountStatusPayload, string>> {
        return this._service
            .mutate<MutationUpdateAccountStatusArgs>(this._mutationRequestBuilder.getUpdateAccountStatusMutation(), {
                bo_user_id: payload.boUserId,
                is_enabled: !payload.isLocked,
            })
            .pipe(
                map(({responsePayload, ...response}) => {
                    return {
                        ...response,
                        requestPayload: payload,
                        responsePayload: responsePayload.updateAccountStatus,
                    };
                })
            );
    }

    unlinkAgent(payload: BoUserUnlinkAgentPayload): Observable<ServiceResponsePayload<BoUserUnlinkAgentPayload, string>> {
        return this._service
            .mutate<MutationUnlinkAgentArgs>(this._mutationRequestBuilder.getUnlinkAgentMutation(), {
                bo_user_id: payload.boUserId,
                uid: payload.uid,
            })
            .pipe(
                map(({responsePayload, ...response}) => {
                    return {
                        ...response,
                        requestPayload: payload,
                        responsePayload: responsePayload.unlinkAgent,
                    };
                })
            );
    }

    getErrorSeverity(_code: BaseHttpCodes): number {
        return 0;
    }
}
